import { InputAdornment } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSystemConfig } from '../../../../hooks/useSystemConfig';
import { DataItem, updateDailyTempWorker } from '../../../../services/apiSLTP/activities';
import { createDailyTempWorker, DailyTempWorker, getTempWorkerTypes } from '../../../../services/apiSLTP/planning';
import { capitalizeFirstLetter, formatName } from '../../../../utils/capitalize';
import { getPriceTypeInfos, priceTypeEnum, stepModes, systemConfigKeyEnum } from '../../../../utils/enums';
import CustomTextInput from '../../../CustomTextInput';
import DropDown, { OptionType } from '../../../DropDown';
import IconButton from '../../../shared/IconButton';
import ModalBody, { ModalProps } from '../../ModalBody';

interface ManageDailyTempWorkerModalProps extends ModalProps {
    activity: DataItem;
    refetch: () => void;
    dailyTempWorker: DailyTempWorker | null;
}

const ManageDailyTempWorkerModal: React.FC<ManageDailyTempWorkerModalProps> = ({ open, handleClose, activity, refetch, dailyTempWorker }) => {
    const { t } = useTranslation();
    const { getConfigValue } = useSystemConfig();
    const [tempWorkersTypes, setTempWorkersTypes] = useState<{ label: string; value: string }[]>([]);
    const [selectedTempWorkerType, setSelectedTempWorkerType] = useState({ label: '', value: '' });
    const [currentTempWorkerId, setCurrentTempWorkerId] = useState(dailyTempWorker?._id);
    const [tempWorkerName, setTempWorkerName] = useState('');
    const [durationHours, setDurationHours] = useState('7');
    const [price, setPrice] = useState(typeof dailyTempWorker?.price === 'number' ? dailyTempWorker?.price.toString() : '');
    const [priceType, setPriceType] = useState<OptionType | null>(
        dailyTempWorker?.priceType
            ? {
                  label: capitalizeFirstLetter(t(getPriceTypeInfos(dailyTempWorker?.priceType))),
                  value: (dailyTempWorker?.priceType).toString(),
              }
            : null
    );

    const fetchTempWorkersTypes = async () => {
        const response = await getTempWorkerTypes();
        setTempWorkersTypes(
            response.data.map((value) => {
                return { label: value.designation, value: value._id };
            })
        );
    };

    const postDailyTempWorker = async () => {
        const response = await createDailyTempWorker({
            activityId: activity._id,
            tempWorkerTypeId: selectedTempWorkerType.value,
            durationHours: parseFloat(durationHours),
            tempWorkerName: tempWorkerName,
            price: price ? parseFloat(price) : undefined,
            priceType: priceType?.value ? parseInt(priceType.value) : undefined,
        });
        setCurrentTempWorkerId(response.data._id);
        refetch();
    };

    const patchDailyTempWorker = async () => {
        if (currentTempWorkerId) {
            const response = await updateDailyTempWorker(currentTempWorkerId, {
                tempWorkerTypeId: selectedTempWorkerType.value,
                durationHours: parseFloat(durationHours),
                tempWorkerName: tempWorkerName,
                price: price ? parseFloat(price) : null,
                priceType: priceType?.value ? parseInt(priceType.value) : null,
            });
            refetch();
        }
    };

    useEffect(() => {
        fetchTempWorkersTypes();
        if (dailyTempWorker) {
            setSelectedTempWorkerType({
                label: dailyTempWorker.tempWorkerTypeId?.designation || '',
                value: dailyTempWorker.tempWorkerTypeId?._id || '',
            });
            setTempWorkerName(dailyTempWorker.tempWorkerName || '');
            setDurationHours((dailyTempWorker.durationHours ?? 7).toString());
        }
    }, []);

    return (
        <ModalBody
            closeText={t('cancel')}
            title={t('tempWorker')}
            subTitle={!!currentTempWorkerId?.length ? t('update') : t('add')}
            description={activity.siteId.name}
            okText={t('validate')}
            open={open}
            handleClose={handleClose}
            handleOk={() => {
                !currentTempWorkerId?.length ? postDailyTempWorker() : patchDailyTempWorker();
            }}
            disabledOk={!(selectedTempWorkerType.value.length && tempWorkerName.length && durationHours.length)}
        >
            <DropDown
                selectedItem={selectedTempWorkerType}
                items={tempWorkersTypes}
                handleChange={(event, value) => setSelectedTempWorkerType({ value: value?.value || '', label: value?.label || '' })}
                inputLabel={t('company')}
                required
            />
            <CustomTextInput
                value={tempWorkerName}
                onChange={(text) => setTempWorkerName(text)}
                label={t('tempWorker')}
                required
                placeholder={`${formatName('bertrand', 'dupont')}`}
            />

            <CustomTextInput
                label={capitalizeFirstLetter(t('durationHours'))}
                type="number"
                value={durationHours}
                onChange={(text) => setDurationHours(text)}
                required
                startAdornment={
                    <InputAdornment position="start">
                        <IconButton
                            iconName="remove"
                            disabled={parseFloat(durationHours) <= 0}
                            onClick={() => setDurationHours(Math.max(0, (parseFloat(durationHours) || 0.5) - 0.5).toString())}
                        />
                    </InputAdornment>
                }
                endAdornment={
                    <InputAdornment position="end">
                        <IconButton
                            iconName="add"
                            disabled={parseFloat(durationHours) >= 24}
                            onClick={() => setDurationHours(Math.min(24, (parseFloat(durationHours) || 0) + 0.5).toString())}
                        />
                    </InputAdornment>
                }
            />
            {activity.stepMode === stepModes.VALIDATE && (
                <>
                    <CustomTextInput
                        value={price}
                        onChange={setPrice}
                        label={t('price')}
                        errorText={t('error')}
                        startAdornment={<div>{getConfigValue(systemConfigKeyEnum.CURRENCY_SYMBOL)}</div>}
                        type="number"
                    />
                    <DropDown
                        items={Object.values(priceTypeEnum).map((type) => ({
                            label: capitalizeFirstLetter(t(getPriceTypeInfos(type))),
                            value: type.toString(),
                        }))}
                        selectedItem={priceType}
                        handleChange={(e, value) => {
                            setPriceType(value);
                        }}
                        inputLabel={t('pricing')}
                        disableClearable={false}
                    />
                </>
            )}
        </ModalBody>
    );
};

export default ManageDailyTempWorkerModal;
